import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-clay-800 border border-terracotta/15 rounded-xl px-4 py-3.5 text-sm text-cream placeholder:text-cream/35 focus:outline-none focus:border-terracotta/60 transition-colors duration-300";

export default function Reservation() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: "", time: "19:00", party_size: 2, notes: "" });
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sendingContact, setSendingContact] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setC = (k) => (e) => setContact({ ...contact, [k]: e.target.value });

  const submitReservation = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reservations`, { ...form, party_size: Number(form.party_size) });
      toast.success(`Table for ${form.party_size} requested — we'll confirm by phone shortly.`);
      setForm({ name: "", phone: "", email: "", date: "", time: "19:00", party_size: 2, notes: "" });
    } catch {
      toast.error("Could not send the request. Please call us at " + SITE.phone);
    } finally {
      setSending(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setSendingContact(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success("Message sent — gracias! We'll be in touch.");
      setContact({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send the message. Please try again.");
    } finally {
      setSendingContact(false);
    }
  };

  return (
    <section id="reserve" className="relative py-28 sm:py-36 bg-clay-900/40" data-testid="reservation-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 max-w-2xl"
        >
          <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-terracotta mb-6" data-testid="reservation-overline">
            Reservations & Contact
          </p>
          <h2 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-cream leading-[1.05]" data-testid="reservation-title">
            Your table is <span className="italic text-terracotta">already waiting</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          <motion.form
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={submitReservation}
            className="lg:col-span-3 bg-clay-800/60 border border-terracotta/15 rounded-3xl p-8 sm:p-10"
            data-testid="reservation-form"
          >
            <div className="flex items-center gap-3 mb-8">
              <CalendarCheck className="w-5 h-5 text-terracotta" />
              <h3 className="font-serif-d text-2xl font-semibold text-cream">Book a Table</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="reservation-name-input" />
              <input required value={form.phone} onChange={set("phone")} placeholder="Phone number" className={inputCls} data-testid="reservation-phone-input" />
              <input value={form.email} onChange={set("email")} type="email" placeholder="Email (optional)" className={inputCls} data-testid="reservation-email-input" />
              <select
                value={form.party_size}
                onChange={set("party_size")}
                className={`${inputCls} appearance-none`}
                data-testid="reservation-party-select"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map((n) => (
                  <option key={n} value={n} className="bg-clay-900">{n} {n === 1 ? "guest" : "guests"}</option>
                ))}
              </select>
              <input required type="date" value={form.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} data-testid="reservation-date-input" />
              <input required type="time" value={form.time} onChange={set("time")} className={`${inputCls} [color-scheme:dark]`} data-testid="reservation-time-input" />
              <textarea
                value={form.notes}
                onChange={set("notes")}
                placeholder="Special requests — birthdays, high chairs, allergies…"
                rows={3}
                className={`${inputCls} sm:col-span-2 resize-none`}
                data-testid="reservation-notes-input"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="mt-8 w-full sm:w-auto px-10 py-4 rounded-full bg-terracotta text-obsidian font-semibold text-sm hover:bg-terracotta-hover transition-colors duration-300 disabled:opacity-60"
              data-testid="reservation-submit-btn"
            >
              {sending ? "Sending…" : "Request Reservation"}
            </button>
          </motion.form>

          <motion.form
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={submitContact}
            className="lg:col-span-2 bg-clay-800/60 border border-terracotta/15 rounded-3xl p-8 sm:p-10"
            data-testid="contact-form"
          >
            <div className="flex items-center gap-3 mb-8">
              <MessageSquare className="w-5 h-5 text-terracotta" />
              <h3 className="font-serif-d text-2xl font-semibold text-cream">Say Hola</h3>
            </div>
            <div className="space-y-4">
              <input required value={contact.name} onChange={setC("name")} placeholder="Your name" className={inputCls} data-testid="contact-name-input" />
              <input required type="email" value={contact.email} onChange={setC("email")} placeholder="Email address" className={inputCls} data-testid="contact-email-input" />
              <textarea
                required
                value={contact.message}
                onChange={setC("message")}
                placeholder="Catering, private events, feedback — tell us everything."
                rows={5}
                className={`${inputCls} resize-none`}
                data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={sendingContact}
              className="mt-8 w-full px-10 py-4 rounded-full border border-terracotta/50 text-terracotta font-semibold text-sm hover:bg-terracotta hover:text-obsidian transition-colors duration-300 disabled:opacity-60"
              data-testid="contact-submit-btn"
            >
              {sendingContact ? "Sending…" : "Send Message"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
